import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import bowser from 'bowser';

import { STATUSES, SUPPORTED_BROWSERS, ITEM_TYPES } from '../config/constants';
import { buyMarketItem, sendAnalyticsEvent } from '../utils/Utils';
import { authRef } from '../config/firebase';
import { network, contracts } from '../config/blockchain';
import { requestWeb3AccountAccess } from '../config/web3';
import { fetchGameUser, fetchTermsAgreed, setTermsAgreed } from '../redux/actions/firebaseActions';
import { fetchMarketItems } from '../redux/actions/marketItemsActions';
import Log from '../utils/Log';

export const withBuy = (WrappedComponent) => {
  class Buy extends PureComponent {
    constructor(props) {
      super(props);

      this.state = {
        showTerms: false,
        termsCallback: null,
        showSupportedBrowsers: false,
        showDownloadMetaMask: false,
        showUnlockMetaMask: false,
        showBuyFail: false,
        buyFailMessage: '',
        showConfirmTransaction: false,
        showTransactionPending: false,
        transactionHash: '',
        showTransactionConfirmed: false,
        showItemPurchased: false,
        showSaleFinished: false,
        events: [],
        buyButtonDisabled: false,
        marketItem: null,
      };
    }

    componentWillMount() {
      this.watchEvents();
      this.props.fetchGameUser();
      this.props.fetchMarketItems();
    }

    componentWillUnmount() {
      if (this.state.events.length) {
        this.setState({
          events: this.state.events.filter((marketEvent) => {
            try {
              marketEvent.stopWatching((error, result) => {});
            }
            catch (e) {
              Log.error(`Error in marketEvent.stopWatching: ${e}`);
            }

            return false;
          })
        });
      }
    }

    componentDidUpdate(prevProps) {
      this.watchEvents();
    }

    watchEvents() {
      if ((this.props.status !== STATUSES.FALLBACK || network.fallbackAllowsWatchEvents) &&
        this.props.status !== STATUSES.UNKNOWN &&
        !this.state.events.length &&
        this.props.myWalletAddress) {

        const auctionBoughtFilter = contracts.stickItemTypeAuction.AuctionBought({buyer: this.props.myWalletAddress});
        auctionBoughtFilter.watch((error, result) => {
          if (!error) {
            Log.info(`AuctionBought event triggered. txHash: ${result.transactionHash}`);

            if (result.transactionHash === this.state.transactionHash) {
              this.hideTransactionPending();
              this.showTransactionConfirmed();

              this.sendAnalyticsEvent('ModalDisplayed - Transaction (Confirmed)', 'Auction', Math.round(this.props.web3.fromWei(result.args.price, 'finney')), true);

              // Reset transaction hash to prevent analytics event being sent again
              this.setState({ transactionHash: '' });
            }
          } else {
            Log.error(error);
          }
        });

        const saleBoughtFilter = contracts.stickItemTypeSale.SaleBought({buyer: this.props.myWalletAddress});
        saleBoughtFilter.watch((error, result) => {
          if (!error) {
            Log.info(`SaleBought event triggered. txHash: ${result.transactionHash}`);

            if (result.transactionHash === this.state.transactionHash) {
              this.hideTransactionPending();
              this.showTransactionConfirmed();

              this.sendAnalyticsEvent('ModalDisplayed - Transaction (Confirmed)', 'Sale', Math.round(this.props.web3.fromWei(result.args.price, 'finney')), true);

              // Reset transaction hash to prevent analytics event being sent again
              this.setState({ transactionHash: '' });
            }
          } else {
            Log.error(error);
          }
        });

        this.setState({
          events: [
            auctionBoughtFilter,
            saleBoughtFilter,
          ]
        });
      }
    }

    showTerms(callback) {
      this.setState({
        showTerms: true,
        termsCallback: callback
      });
    }

    hideTerms() {
      this.setState({
        showTerms: false
      });
    }

    setTermsAgreed(agree) {
      window.localStorage.setItem('termsAgreed', true);

      if (this.props.gameUser) {
        this.props.setTermsAgreed(authRef.currentUser.uid, agree);
      }

      this.state.termsCallback();
    }

    showSupportedBrowsers() {
      this.setState({
        showSupportedBrowsers: true
      });
    }

    hideSupportedBrowsers() {
      this.setState({
        showSupportedBrowsers: false
      });
    }

    showDownloadMetaMask() {
      this.setState({
        showDownloadMetaMask: true
      });
    }

    hideDownloadMetaMask() {
      this.setState({
        showDownloadMetaMask: false
      });
    }

    showUnlockMetaMask() {
      this.setState({
        showUnlockMetaMask: true
      });
    }

    hideUnlockMetaMask() {
      this.setState({
        showUnlockMetaMask: false
      });
    }

    showBuyFail(message) {
      this.setState({
        buyFailMessage: message,
        showBuyFail: true
      });
    }

    hideBuyFail() {
      this.setState({
        buyFailMessage: '',
        showBuyFail: false
      });
    }

    showConfirmTransaction() {
      this.setState({
        showConfirmTransaction: true
      });
    }

    hideConfirmTransaction() {
      this.setState({
        showConfirmTransaction: false
      });
    }

    showTransactionPending() {
      this.setState({
        showTransactionPending: true
      });
    }

    hideTransactionPending() {
      this.setState({
        showTransactionPending: false
      });
    }

    showTransactionConfirmed() {
      this.setState({
        showTransactionConfirmed: true
      });
    }

    hideTransactionConfirmed() {
      this.setState({
        showTransactionConfirmed: false
      });
    }

    itemPurchased() {
      this.setState({
        showItemPurchased: true
      });
    }

    saleFinished() {
      this.setState({
        showSaleFinished: true
      });

      this.disableBuyButton();
    }

    enableBuyButton() {
      this.setState({
        buyButtonDisabled: false
      });
    }

    disableBuyButton() {
      this.setState({
        buyButtonDisabled: true
      });
    }

    checkBrowser() {
      const browser = bowser.getParser(window.navigator.userAgent).getBrowserName();

      Log.info(`Current browser name is "${browser}."`);

      if (browser && SUPPORTED_BROWSERS.indexOf(browser) === -1) {
        this.sendAnalyticsEvent('ModalDisplayed - Alert (UnsupportedBrowser)');

        this.showSupportedBrowsers();
      } else {
        this.sendAnalyticsEvent('ModalDisplayed - MetaMask (Absent)');

        this.showDownloadMetaMask();
      }
    }

    requestWeb3Unlock(callback) {
      const accessGranted = () => {
        this.hideUnlockMetaMask();
        callback();
      };

      this.showUnlockMetaMask();
      requestWeb3AccountAccess(accessGranted);
    }

    buyMarketItemViaWeb3(marketItem) {
      const buyCallback = () => {
        this.sendAnalyticsEvent('ModalDisplayed - MetaMask (ExtensionPrompt)');

        this.showConfirmTransaction();
        this.enableBuyButton();
      };

      buyMarketItem(
        marketItem,
        this.props.myWalletAddress,
        this.props.web3,
        (this.props.gameUser) ? authRef.currentUser.uid : 'UnknownUserUID',
        buyCallback
      ).then((txHash) => {
        Log.info('Item purchase transaction started on blockchain.');

        this.sendAnalyticsEvent('ModalDisplayed - MetaMask (ExtensionActivityConfirm)');
        this.sendAnalyticsEvent('ModalDisplayed - Transaction (Pending)', 'UnknownSaleType', 0, true);

        this.hideConfirmTransaction();
        this.showTransactionPending();
        this.setState({ transactionHash: txHash });
      }, (error) => {
        this.hideConfirmTransaction();
        this.enableBuyButton();

        if (error) {
          this.sendAnalyticsEvent('ModalDisplayed - MetaMask (ExtensionActivityError)');
          this.showBuyFail(error);
        } else {
          this.sendAnalyticsEvent('ModalDisplayed - MetaMask (ExtensionActivityReject)');
        }
      });
    }

    buyMarketItem(marketItem) {
      // Prevent double-clicking of buy buttons
      this.disableBuyButton();

      this.setState({
        transactionHash: '',
        marketItem: marketItem
      }, () => {
        this.sendAnalyticsEvent('ButtonClicked - BuyNow');
        this.buyMarketItemTermsCheck();
      });
    }

    buyMarketItemTermsCheck() {
      if (this.props.gameUser) {
        this.props.fetchTermsAgreed(authRef.currentUser.uid).then((termsAgreed) => {
          this.buyMarketItemTermsCallback(termsAgreed);
        });
      } else {
        const termsAgreedLocal = window.localStorage.getItem('termsAgreed');
        this.buyMarketItemTermsCallback(termsAgreedLocal);
      }
    }

    buyMarketItemTermsCallback(agreed) {
      if (agreed) {
        this.buyMarketItemWeb3Check();
      } else {
        const termsCallback = () => {
          this.sendAnalyticsEvent('ButtonClicked - TermsAgreed');
          this.buyMarketItemWeb3Check();
        }

        this.enableBuyButton();
        this.showTerms(termsCallback);
      }
    }

    buyMarketItemWeb3Check() {
      if (this.props.status === STATUSES.FALLBACK) { // No MetaMask
        this.enableBuyButton();
        this.checkBrowser();
      } else if (this.props.status === STATUSES.LOCKED) { // MetaMask locked
        this.sendAnalyticsEvent('ModalDisplayed - MetaMask (Locked)');
        this.enableBuyButton();
        this.requestWeb3Unlock(() => this.buyMarketItemWeb3Check());
      } else { // STATUSES.AVAILABLE
        this.buyMarketItemViaWeb3(this.state.marketItem);
      }
    }

    sendAnalyticsEvent(eventAction, eventSaleType = 'UnknownSaleType', eventValue = 0, appendWalletAddressToLabel = false) {
      if (eventSaleType === 'UnknownSaleType' && this.state.marketItem) {
        eventSaleType = (this.state.marketItem.type === ITEM_TYPES.AUCTION) ? 'Auction' : 'Sale'
      }

      const itemName = (this.state.marketItem) ? this.state.marketItem.schema.properties.name.description : 'UnknownItemName';
      let eventLabel = `${itemName} - ${(this.props.gameUser) ? authRef.currentUser.uid : 'UnknownUserUID'}`;

      if (appendWalletAddressToLabel && this.props.myWalletAddress) {
        eventLabel += ` - ${this.props.myWalletAddress}`;
      }

      sendAnalyticsEvent(
        'PurchaseFlow',
        `${eventSaleType} - ${eventAction}`,
        eventLabel,
        eventValue
      );
    }

    render() {
      return (
        <WrappedComponent
          showTerms={this.state.showTerms}
          hideTerms={this.hideTerms.bind(this)}
          setTermsAgreed={this.setTermsAgreed.bind(this)}
          showSupportedBrowsers={this.state.showSupportedBrowsers}
          hideSupportedBrowsers={this.hideSupportedBrowsers.bind(this)}
          showDownloadMetaMask={this.state.showDownloadMetaMask}
          hideDownloadMetaMask={this.hideDownloadMetaMask.bind(this)}
          showUnlockMetaMask={this.state.showUnlockMetaMask}
          hideUnlockMetaMask={this.hideUnlockMetaMask.bind(this)}
          gameUser={this.props.gameUser}
          auctionItems={this.props.auctionItems}
          saleItems={this.props.saleItems}
          buyMarketItem={this.buyMarketItem.bind(this)}
          showBuyFail={this.state.showBuyFail}
          hideBuyFail={this.hideBuyFail.bind(this)}
          buyFailMessage={this.state.buyFailMessage}
          showConfirmTransaction={this.state.showConfirmTransaction}
          hideConfirmTransaction={this.hideConfirmTransaction.bind(this)}
          showTransactionPending={this.state.showTransactionPending}
          hideTransactionPending={this.hideTransactionPending.bind(this)}
          showTransactionConfirmed={this.state.showTransactionConfirmed}
          hideTransactionConfirmed={this.hideTransactionConfirmed.bind(this)}
          showItemPurchased={this.state.showItemPurchased}
          itemPurchased={this.itemPurchased.bind(this)}
          showSaleFinished={this.state.showSaleFinished}
          saleFinished={this.saleFinished.bind(this)}
          transactionHash={this.state.transactionHash}
          buyButtonDisabled={this.state.buyButtonDisabled}
        />
      );
    }
  }

  const mapStateToProps = state => {
    return {
      web3: state.web3Reducer.web3,
      status: state.web3Reducer.status,
      myWalletAddress: state.web3Reducer.myWalletAddress,
      gameUser: state.firebaseReducer.gameUser,
      auctionItems: state.auctionItemsReducer.auctionItems,
      saleItems: state.saleItemsReducer.saleItems,
    }
  }

  const mapDispatchToProps = {
    fetchGameUser,
    fetchMarketItems,
    fetchTermsAgreed,
    setTermsAgreed
  }

  return connect(mapStateToProps, mapDispatchToProps)(Buy);
}

export default withBuy;



// WEBPACK FOOTER //
// ./src/components/withBuy.js